import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, Subject, takeUntil } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  INotification,
  IReply,
  IUpdateNotification,
  ISetNotificationCompleted,
  IUpdateNotificationPin,
  IUpdateReply,
} from '../models/template/notifications-model.interface';
import { TranslateService } from './translate.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  isTranslator: boolean | undefined;

  private _destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private translateService: TranslateService
  ) {
    this.translateService.isTranslator$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (res) => {
          this.isTranslator = res;
        },
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  // host url
  private getHostUrl = () => {
    const url = environment.scheme + environment.host + environment.port + '/';
    return url;
  };
  // defining endpoints

  public readonly getNotificationUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/getNotification`;

  public readonly createNotificationUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/addNotification`; ///todo : rename to createNotification ,on backend too !!!
  public readonly updateNotificationUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/updateNotification`;
  public readonly addReplyUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/createReply`;
  public readonly deleteReplyUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/deleteReply`;
  public readonly deleteNotificationUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/deleteNotification`;

  public readonly getTemplateNotificationsUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/getTemplateNotifications`;
  public readonly setCompletedUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/setCompleted`;
  public readonly updatePinUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/updatePin`;
  public readonly updateReplyUrl = `${this.getHostUrl()}api/v${
    environment.version
  }/Notifications/updateReply`;

  getNotification(blockId: string): Observable<any> {
    let params = new HttpParams().set('blockId', blockId);
    params = this.translateService.setTranslatorParams(
      this.isTranslator!,
      params
    );
    return this.http.get<any>(this.getNotificationUrl, {params});
  }

  createNotification(createNotificationData: INotification): Observable<any> {
    // console.info("Create notification data : ", createNotificationData)

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.post<any>(
      this.createNotificationUrl,
      createNotificationData,
      {params}
    );
  }

  updateNotification(
    notificationId: string,
    updateNotificationData: IUpdateNotification
  ): Observable<any> {
    const url = `${this.updateNotificationUrl}/${notificationId}/`;

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.put<any>(url, updateNotificationData, {params});
  }

  deleteNotification(notificationId: string, blockId: string): Observable<any> {
    const url = `${this.deleteNotificationUrl}/${notificationId}/${blockId}`;

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.delete<any>(url, {params});
  }

  createReply(notificationId: string, replyData: IReply): Observable<any> {
    const url = `${this.addReplyUrl}/${notificationId}/`;

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.post<any>(url, replyData, {params});
  }

  deleteReply(notificationId: string, replyId: string): Observable<any> {
    const url = `${this.deleteReplyUrl}/${notificationId}/${replyId}`;

    const params = this.translateService.setTranslatorParams(
      this.isTranslator!
    );

    return this.http.delete<any>(url, {params});
  }

  getTemplateNotifications(templateId: string): Observable<INotification[]> {
    let params = new HttpParams().set('templateId', templateId);
    params = this.translateService.setTranslatorParams(
      this.isTranslator!,
      params
    );
    return this.http.get<INotification[]>(this.getTemplateNotificationsUrl, { params });
  }

  setCompleted(notificationId: string, data: ISetNotificationCompleted): Observable<INotification> {
    const url = `${this.setCompletedUrl}/${notificationId}`;
    const params = this.translateService.setTranslatorParams(this.isTranslator!);
    return this.http.put<INotification>(url, data, { params });
  }

  updatePin(notificationId: string, data: IUpdateNotificationPin): Observable<INotification> {
    const url = `${this.updatePinUrl}/${notificationId}`;
    const params = this.translateService.setTranslatorParams(this.isTranslator!);
    return this.http.put<INotification>(url, data, { params });
  }

  updateReply(notificationId: string, replyId: string, data: IUpdateReply): Observable<INotification> {
    const url = `${this.updateReplyUrl}/${notificationId}/${replyId}`;
    const params = this.translateService.setTranslatorParams(this.isTranslator!);
    return this.http.put<INotification>(url, data, { params });
  }
}
